import dayjs from "dayjs";
import Holidays from "date-holidays";
import { FullProperty } from "../../models/fullProperty";

export const TimeServices = {

  isClosed: (listing?: FullProperty) => {
    if (!listing) return true;

    // Get the current moment.
    const now = new Date();
    let closingTime: string | undefined;
    const operatingDay = TimeServices.getOperatingDayType(now);
    switch (operatingDay) {
      case 'public-holiday':
        closingTime = listing.closingTime;
        break;
      case 'monday':
        closingTime = listing.mondayEnd;
        break;
      case 'tuesday':
        closingTime = listing.tuesdayEnd;
        break;
      case 'wednesday':
        closingTime = listing.wednesdayEnd;
        break;
      case 'thursday':
        closingTime = listing.thursdayEnd;
        break;
      case 'friday':
        closingTime = listing.fridayEnd;
        break;
      case 'saturday':
        closingTime = listing.saturdayEnd;
        break;
      case 'sunday':
        closingTime = listing.sundayEnd;
        break;
      default:
        closingTime = listing.closingTime;
        break;
    }

    // Parse the provided full timestamp (we only need time-of-day parts).
    const parsedClosing = dayjs(closingTime);
    const closingHour = parsedClosing.hour();
    const closingMinute = parsedClosing.minute();
    const closingSecond = parsedClosing.second();
    const nowjs = dayjs();
    // Build a Day.js object for today's closing time using the parsed hour, minute, and second.
    let closingMoment = nowjs
      .set("hour", closingHour)
      .set("minute", closingMinute)
      .set("second", closingSecond)
      .set("millisecond", 0);

    // If the closing hour is in the early hours (before 6 AM),
    // determine which day that closing time should be associated with.
    //
    // For example:
    //   - If it's currently after 6 AM (e.g., 23:00), then a closing time of 2 AM
    //     logically belongs to the next calendar day.
    //   - If it's already early morning (e.g., 1 AM), then today’s 2 AM is the correct cutoff.
    if (closingHour < 6) {
      if (nowjs.hour() >= 6) {
        // After 6 AM, we consider the "closing moment" to be on the following day.
        closingMoment = closingMoment.add(1, "day");
      }
    }

    // If the current time is after the calculated closing moment, then the restaurant is closed.
    return nowjs.isAfter(closingMoment);
  },

  formatDate: (date: dayjs.Dayjs) => {
    const today = dayjs().startOf("day");
    if (date.isSame(today, "day")) return `Today ${date.format("h:mm A")}`;
    if (date.isSame(today.add(1, "day"), "day")) return `Tomorrow ${date.format("h:mm A")}`;
    if (date.isBefore(today, "day")) return date.format("h:mm A");
    return date.format("YYYY-MM-DD : h:mm A");
  },

  // Sunday = 0, Saturday = 6
  isWeekend: (date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6;
  },

  isWeekday: (date: Date): boolean => !TimeServices.isWeekend(date),


  isPublicHoliday: (date: Date): boolean => {
    const hd = new Holidays('ZA');
    return !!hd.isHoliday(date);
  },

  getOperatingDayType: (date: Date): 'public-holiday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday' => {
    if (TimeServices.isPublicHoliday(date)) return 'public-holiday';

    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    return dayOfWeek as
      | 'monday'
      | 'tuesday'
      | 'wednesday'
      | 'thursday'
      | 'friday'
      | 'saturday'
      | 'sunday';
  },

  getCurrentClosingTime: (endTime: Date): Date => {
    const now = new Date();
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      endTime.getHours(),
      endTime.getMinutes(),
      endTime.getSeconds(),
      endTime.getMilliseconds()
    );

  },

  getCurrentEndDateTime: (operatingDay: string, restaurant: FullProperty): string => {
    const now = new Date();
    switch (operatingDay) {
      case 'public-holiday':
        return restaurant.closingTime;
      case 'monday':
        return restaurant.mondayEnd;
      case 'tuesday':
        return restaurant.tuesdayEnd;
      case 'wednesday':
        return restaurant.wednesdayEnd;
      case 'thursday':
        return restaurant.thursdayEnd;
      case 'friday':
        return restaurant.fridayEnd;
      case 'saturday':
        return restaurant.saturdayEnd;
        break;
      case 'sunday':
        return restaurant.sundayEnd;
      default:
        return restaurant.closingTime;
    }
  },

  getCurrentStartDateTime: (operatingDay: string, restaurant: FullProperty): string => {
    const now = new Date();
    switch (operatingDay) {
      case 'public-holiday':
        return restaurant.closingTime;
      case 'monday':
        return restaurant.mondayStart;
      case 'tuesday':
        return restaurant.tuesdayStart;
      case 'wednesday':
        return restaurant.wednesdayStart;
      case 'thursday':
        return restaurant.thursdayStart;
      case 'friday':
        return restaurant.fridayStart;
      case 'saturday':
        return restaurant.saturdayStart;
        break;
      case 'sunday':
        return restaurant.sundayStart;
      default:
        return restaurant.closingTime;
    }
  },

 isRestaurantClosed(operatingDay: string, restaurant: FullProperty): boolean {
  switch (operatingDay) {
    case "public-holiday":
      return restaurant.offlineHoliday;
    case "monday":
      return restaurant.offlineMonday;
    case "tuesday":
      return restaurant.offlineTuesday;
    case "wednesday":
      return restaurant.offlineWednesday;
    case "thursday":
      return restaurant.offlineThursday;
    case "friday":
      return restaurant.offlineFriday;
    case "saturday":
      return restaurant.offlineSaturday;
    case "sunday":
      return restaurant.offlineSunday;
    default:
      return false;
  }
}
};